import { Root } from "@radix-ui/react-accordion";
import { keyBy } from "lodash";
import React, { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { AgentPromptEditor } from "src/agentNode/AgentPromptEditor";
import { AgentSelectionEditor } from "src/agentNode/AgentSelectionEditor";
import { AgentEnvironmentConfig } from "src/agentNode/components/AgentEnvironmentConfig";
import { AgentInputOutputMappings } from "src/agentNode/components/AgentInputOutputMappings";
import { useAgentSchemas, useAgentUpdateKnownEtag } from "src/agentNode/hooks";
import { AgentNodeForm } from "src/agentNode/types";
import { setupAgentNodeForm } from "src/agentNode/utils";
import { useAgent, useAgentVersion } from "src/agents/queries/management";
import { Agent, AgentVersion } from "src/agents/types";
import { FieldErrors } from "src/api/types";
import {
  accordionRootClassName,
  EditorAccordionItem as AccordionItem,
} from "src/base-components/EditorAccordionItem";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import { AgentNode, AgentNodeData } from "src/constants/NodeDataTypes";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { useFormApplyNodeSuggestion } from "src/nodeEditor/applyNodeSuggestion";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";
import { useSubmitForm } from "src/utils/useSubmitForm";

type Props = {
  selectedNode: AgentNode;
} & NodeEditorBaseProps<AgentNodeData>;

const usePreviousValues = (nodeData: AgentNodeData) => {
  return useMemo(() => nodeData, [nodeData]);
};

export const AgentNodeEditor: React.FC<Props> = ({
  selectedNode,
  immutable,
  isReactive,
  displayedError,
  onUpdate,
}) => {
  const { renderedInDiffView } = useDiffViewContext();

  const nodeData = usePreviousValues(selectedNode.data);
  const nodeId = selectedNode.id;

  const fieldErrors: FieldErrors | undefined = displayedError?.field_errors
    ? convertFieldErrorsBeToFe(displayedError.field_errors, true)
    : undefined;

  // Fetch agent and version data
  const agentQuery = useAgent(nodeData.agent_id ?? "");
  const versionQuery = useAgentVersion(
    nodeData.agent_id ?? "",
    nodeData.agent_version_id ?? "",
  );
  const agentSchemas = useAgentSchemas(versionQuery.data);

  const hasAgentSelected = Boolean(
    nodeData.agent_id && nodeData.agent_version_id,
  );

  // Keep agent_version_known_etag in sync with the actual agent version etag
  useAgentUpdateKnownEtag({
    nodeId,
    nodeData,
    immutable,
    isReactive,
    onUpdate,
    etag: versionQuery.data?.etag,
  });

  // Setup form with schema-aware mappings (similar to LoopNodeEditor)
  const [defaultValues, schemaMappingErrors, fieldsToRemove] = useMemo(() => {
    // Only setup the form once the agent version has been fetched
    if (immutable || versionQuery.data === undefined) {
      return [nodeData, [], []];
    }
    return setupAgentNodeForm(nodeData, agentSchemas);
  }, [immutable, versionQuery.data, nodeData, agentSchemas]);

  // Filter errors by type for input mappings
  const inputMappingErrors = keyBy(
    schemaMappingErrors.filter(
      (e) => e.type === "input" && e.issue === "field-missing",
    ),
    "fieldName",
  );

  const formMethods = useForm<AgentNodeForm>({
    defaultValues,
    ...(isReactive && { values: defaultValues }),
  });

  const handleSubmitRef = useRef<VoidFunction>(() => {});

  // Keep ref up to date with current submit handler
  handleSubmitRef.current = formMethods.handleSubmit((data) => {
    onUpdate({
      newData: {
        ...nodeData,
        ...data,
      },
    });
  });

  // Handle field removal when schema changes (similar to LoopNodeEditor)
  const { unregister } = formMethods;
  useEffect(() => {
    // Prevent unnecessary submits because they e.g. result in change history entries
    if (fieldsToRemove.length > 0) {
      unregister(fieldsToRemove);
      if (!isReactive && !immutable) {
        handleSubmitRef.current();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsToRemove.join(","), unregister, isReactive, immutable]);

  useFormApplyNodeSuggestion<AgentNodeData>(nodeId, (suggestion) => {
    if (suggestion.prompt) {
      formMethods.setValue("prompt", suggestion.prompt);
    }
  });

  const handleSelectAgent = (version: AgentVersion, agent: Agent) => {
    // When a flow version is selected we want to immediately update the node
    const newData = {
      ...nodeData,
      ...formMethods.getValues(),
      agent_id: agent.id,
      agent_version_known_etag: version.etag,
      agent_version_id: version.id,
    };
    onUpdate({
      newData,
    });
  };

  useSubmitForm({
    onChange: (data: AgentNodeForm) => {
      onUpdate({ newData: { ...nodeData, ...data } });
    },
    disabled: isReactive || immutable,
    previousValues: defaultValues,
    watch: formMethods.watch,
  });

  return (
    <FormProvider {...formMethods}>
      <Root
        className={accordionRootClassName}
        defaultValue={["agent", "mappings", "advanced"]}
        type="multiple"
      >
        <AccordionItem
          disabled={renderedInDiffView}
          title="Select the Agent you want to call"
          value="agent"
        >
          <div className="pb-4">
            <AgentSelectionEditor
              handleSelectAgent={handleSelectAgent}
              immutable={immutable}
            />
          </div>
        </AccordionItem>

        <AccordionItem
          disabled={renderedInDiffView}
          title="Map inputs and outputs of the Agent"
          value="mappings"
        >
          <div className="space-y-4 pb-4">
            {hasAgentSelected ? (
              <AgentInputOutputMappings
                agentId={nodeData.agent_id ?? ""}
                agentName={agentQuery.data?.name ?? "Agent"}
                immutable={immutable}
                inputMappingErrors={inputMappingErrors}
                inputSchema={agentSchemas.input}
                isLoading={versionQuery.isLoading}
                versionId={nodeData.agent_version_id ?? ""}
              />
            ) : (
              <p className="text-gray-500 font-inter-normal-13px">
                Input and output mapping will be available after selecting an
                Agent.
              </p>
            )}
            <AgentPromptEditor
              immutable={immutable}
              isReactive={isReactive}
              runFieldErrors={fieldErrors}
            />
          </div>
        </AccordionItem>

        <AccordionItem
          disabled={renderedInDiffView}
          title="Advanced settings"
          value="advanced"
        >
          <div className="pb-4">
            <AgentEnvironmentConfig disabled={immutable} />
          </div>
        </AccordionItem>
      </Root>
    </FormProvider>
  );
};
